'use client';

import { setTag, setUser, setExtra } from '@sentry/nextjs';
import { useEffect, useMemo, useState, type PropsWithChildren } from 'react';
import { UserContext } from '@/contexts/userContext';
import { PreferencesKey } from '@/data/dal/preferencesKey';
import { useEnvironment } from './useEnvironment';
import { useNonProjectDal } from './useNonProjectDal';
export const UserProvider = ({
  children
}: PropsWithChildren) => {
  const {
    nonProjectDal: dal
  } = useNonProjectDal();
  const {
    isDemo,
    demoSettings
  } = useEnvironment();
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const {
    data: user,
    isLoading: isLoadingUser
  } = dal.users.useViewer();
  const {
    data: projects,
    isLoading: isLoadingProjects
  } = dal.projects.useList();
  const {
    data: pref,
    isLoading: isLoadingPreferences
  } = dal.preferences.useUserPref((projects?.projects || []).length > 1 ? PreferencesKey.CurrentProject : undefined);
  const initialProjectId_0 = useMemo(() => {
    if (isLoadingUser || isLoadingProjects || isLoadingPreferences || !user) {
      return '';
    }
    let initialProjectId = projects?.projects?.[0]?.id || '';
    if (pref?.value?.projectId && projects?.projects?.some(x => x.id === pref.value!.projectId)) {
      initialProjectId = pref.value.projectId;
    }
    return initialProjectId;
  }, [isLoadingPreferences, isLoadingProjects, isLoadingUser, pref, projects, user]);
  const currentProjectId = selectedProjectId || initialProjectId_0;
  useEffect(() => {
    // Enrich sentry errors
    setTag('projectId', currentProjectId);
    if (user) {
      setUser({
        id: user.id,
        email: user.email,
        name: user.givenName
      });
      setTag('roleId', user.selectedRole.id);
      setTag('roleName', user.selectedRole.name);
      setTag('eonAccountId', user.eonAccount?.id);
      setExtra('scopes', user.selectedRole.scopes);
      if (isDemo) {
        setExtra('demoSettings', demoSettings);
      }
    }
  }, [currentProjectId, demoSettings, isDemo, user]);
  return <UserContext.Provider value={{
    user,
    isEmployee: Boolean(user?.email?.endsWith('@eon.io') || user?.givenName.endsWith('@eon.io')),
    eonAccount: user?.eonAccount ?? undefined,
    currentProjectId,
    changeProject: newProjectId => dal.preferences.updateUserPref(PreferencesKey.CurrentProject, {
      projectId: newProjectId
    }).then(() => setSelectedProjectId(newProjectId)),
    changeRole: roleId => dal.users.changeRole(roleId).then(() => dal.users.refreshToken(roleId))
  }}>
      {children}
    </UserContext.Provider>;
};